import { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle,
  Users,
  Eye,
  TrendingUp,
  TrendingDown,
  Minus,
  Shield,
  RefreshCw,
  X,
  ChevronRight,
} from 'lucide-react';
import { get, post } from '../lib/api.js';

// ─── Types ───────────────────────────────────────────────────

interface InsiderThreatSignal {
  signal_type: string;
  source: 'saas_posture' | 'shadow_ai' | 'email_access' | 'training';
  points: number;
  description: string;
  detected_at: string;
  raw_ref?: string;
}

interface InsiderThreatScore {
  id: string;
  user_id: string;
  user_email: string;
  user_upn?: string;
  department?: string;
  total_score: number;
  risk_level: 'critical' | 'high' | 'medium' | 'low';
  saas_posture_score: number;
  shadow_ai_score: number;
  email_access_score: number;
  training_score: number;
  signals: InsiderThreatSignal[];
  score_7d_ago?: number;
  score_30d_ago?: number;
  trend_direction: 'increasing' | 'decreasing' | 'stable';
  computed_at: string;
}

interface InsiderThreatAlert {
  id: string;
  user_id: string;
  user_email: string;
  score_at_trigger: number;
  risk_level: string;
  top_signals: InsiderThreatSignal[];
  status: 'open' | 'investigating' | 'resolved' | 'false_positive';
  resolved_by?: string;
  resolved_at?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface SummaryStats {
  total_users: number;
  by_risk_level: { critical: number; high: number; medium: number; low: number };
  high_risk_threshold: number;
  users_above_threshold: number;
  new_alerts_24h: number;
}

// ─── Helpers ─────────────────────────────────────────────────

function riskBadge(risk: string) {
  const map: Record<string, string> = {
    critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    low: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full capitalize ${map[risk] ?? 'bg-gray-100 text-gray-600'}`}>
      {risk}
    </span>
  );
}

function scoreBar(score: number) {
  const color =
    score >= 75 ? 'bg-red-500' :
    score >= 50 ? 'bg-orange-500' :
    score >= 25 ? 'bg-yellow-500' :
    'bg-emerald-500';

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 min-w-[60px]">
        <div
          className={`h-2 rounded-full transition-all ${color}`}
          style={{ width: `${Math.min(score, 100)}%` }}
        />
      </div>
      <span className="text-xs font-medium text-gray-700 dark:text-gray-300 w-12 text-right">
        {score}/100
      </span>
    </div>
  );
}

function sourceBadge(source: InsiderThreatSignal['source']) {
  const map: Record<string, string> = {
    saas_posture: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    shadow_ai: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    email_access: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
    training: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  };
  const labels: Record<string, string> = {
    saas_posture: 'SaaS',
    shadow_ai: 'Shadow AI',
    email_access: 'Email',
    training: 'Training',
  };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded ${map[source] ?? 'bg-gray-100 text-gray-600'}`}>
      {labels[source] ?? source}
    </span>
  );
}

function alertStatusBadge(status: InsiderThreatAlert['status']) {
  const map: Record<string, string> = {
    open: 'bg-red-100 text-red-700',
    investigating: 'bg-yellow-100 text-yellow-700',
    resolved: 'bg-emerald-100 text-emerald-700',
    false_positive: 'bg-gray-100 text-gray-600',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full capitalize ${map[status] ?? 'bg-gray-100 text-gray-600'}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function TrendIcon({ direction }: { direction: InsiderThreatScore['trend_direction'] }) {
  if (direction === 'increasing') return <TrendingUp className="w-4 h-4 text-red-500" />;
  if (direction === 'decreasing') return <TrendingDown className="w-4 h-4 text-emerald-500" />;
  return <Minus className="w-4 h-4 text-gray-400" />;
}

// ─── Detail Panel ─────────────────────────────────────────────

function UserDetailPanel({
  score,
  onClose,
  onRecompute,
}: {
  score: InsiderThreatScore;
  onClose: () => void;
  onRecompute: (userId: string) => Promise<void>;
}) {
  const [recomputing, setRecomputing] = useState(false);

  async function handleRecompute() {
    setRecomputing(true);
    try {
      await onRecompute(score.user_id);
    } finally {
      setRecomputing(false);
    }
  }

  const sortedSignals = [...score.signals].sort((a, b) => b.points - a.points);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white dark:bg-gray-900 shadow-xl h-full overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{score.user_email}</h3>
            {score.department && (
              <p className="text-sm text-gray-500 dark:text-gray-400">{score.department}</p>
            )}
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 px-6 py-5 space-y-6">
          {/* Score overview */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              {riskBadge(score.risk_level)}
              <span className="text-3xl font-bold text-gray-900 dark:text-white">{score.total_score}</span>
              <span className="text-sm text-gray-500 dark:text-gray-400">/ 100</span>
              <TrendIcon direction={score.trend_direction} />
            </div>
            {scoreBar(score.total_score)}
          </div>

          {/* Component breakdown */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Score Components</h4>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'SaaS Posture', value: score.saas_posture_score },
                { label: 'Shadow AI', value: score.shadow_ai_score },
                { label: 'Email / Access', value: score.email_access_score },
                { label: 'Training', value: score.training_score },
              ].map(({ label, value }) => (
                <div key={label} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{value}<span className="text-xs font-normal text-gray-400">/25</span></p>
                </div>
              ))}
            </div>
          </div>

          {/* Score history */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Score History</h4>
            <div className="flex gap-4">
              <div className="text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400">30d ago</p>
                <p className="text-lg font-bold text-gray-700 dark:text-gray-300">{score.score_30d_ago ?? '—'}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-300 self-center" />
              <div className="text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400">7d ago</p>
                <p className="text-lg font-bold text-gray-700 dark:text-gray-300">{score.score_7d_ago ?? '—'}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-300 self-center" />
              <div className="text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400">Current</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">{score.total_score}</p>
              </div>
            </div>
          </div>

          {/* Signal breakdown */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Contributing Signals ({sortedSignals.length})
            </h4>
            {sortedSignals.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No signals detected in the last 30 days.</p>
            ) : (
              <div className="space-y-2">
                {sortedSignals.map((sig, i) => (
                  <div
                    key={`${sig.signal_type}-${i}`}
                    className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {sourceBadge(sig.source)}
                        <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
                          {sig.signal_type}
                        </span>
                      </div>
                      <p className="text-sm text-gray-800 dark:text-gray-200">{sig.description}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                        {new Date(sig.detected_at).toLocaleString()}
                      </p>
                    </div>
                    <span className="text-sm font-bold text-red-600 dark:text-red-400 shrink-0">+{sig.points}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <p className="text-xs text-gray-400 dark:text-gray-500">
            Last computed: {new Date(score.computed_at).toLocaleString()}
          </p>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-6 py-4">
          <button
            onClick={handleRecompute}
            disabled={recomputing}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${recomputing ? 'animate-spin' : ''}`} />
            {recomputing ? 'Recomputing...' : 'Trigger Recompute'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Resolve Modal ────────────────────────────────────────────

function ResolveAlertModal({
  alert,
  onClose,
  onResolved,
}: {
  alert: InsiderThreatAlert;
  onClose: () => void;
  onResolved: () => void;
}) {
  const [status, setStatus] = useState<'resolved' | 'false_positive' | 'investigating'>('resolved');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    setLoading(true);
    setError(null);
    try {
      await post(`/protect/insider-threat/alerts/${alert.id}?action=resolve`, { status, notes: notes || undefined });
      onResolved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resolve alert');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-md p-6 mx-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Resolve Alert</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{alert.user_email}</p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
            {error}
          </div>
        )}

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Resolution</label>
          <select
            value={status}
            onChange={e => setStatus(e.target.value as typeof status)}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
          >
            <option value="resolved">Resolved</option>
            <option value="false_positive">False Positive</option>
            <option value="investigating">Mark as Investigating</option>
          </select>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Notes (optional)</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={3}
            placeholder="Add investigation notes..."
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none resize-none"
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {loading ? 'Saving...' : 'Save'}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

export function InsiderThreatPage() {
  const [scores, setScores] = useState<InsiderThreatScore[]>([]);
  const [alerts, setAlerts] = useState<InsiderThreatAlert[]>([]);
  const [summary, setSummary] = useState<SummaryStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'scores' | 'alerts'>('scores');
  const [riskFilter, setRiskFilter] = useState<string>('');
  const [selectedScore, setSelectedScore] = useState<InsiderThreatScore | null>(null);
  const [resolveAlert, setResolveAlert] = useState<InsiderThreatAlert | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [scoresRes, alertsRes, summaryRes] = await Promise.all([
        get<{ data: InsiderThreatScore[]; hasMore?: boolean }>(
          `/protect/insider-threat${riskFilter ? `?risk_level=${riskFilter}` : ''}`,
        ),
        get<{ data: InsiderThreatAlert[] }>('/protect/insider-threat?action=alerts'),
        get<{ data: SummaryStats }>('/protect/insider-threat?action=summary'),
      ]);
      setScores(scoresRes.data ?? []);
      setAlerts(alertsRes.data ?? []);
      setSummary(summaryRes.data ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load insider threat data');
    } finally {
      setLoading(false);
    }
  }, [riskFilter]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  async function handleRecompute(userId: string) {
    const score = scores.find(s => s.user_id === userId);
    if (!score) return;
    await post(`/protect/insider-threat/${userId}?action=recompute`, { user_email: score.user_email });
    await fetchAll();
    // Refresh selected score
    const refreshed = scores.find(s => s.user_id === userId);
    if (refreshed) setSelectedScore(refreshed);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-red-700 dark:text-red-400">{error}</p>
        <button onClick={fetchAll} className="mt-2 text-sm text-red-600 dark:text-red-400 underline">Retry</button>
      </div>
    );
  }

  const criticalCount = summary?.by_risk_level.critical ?? 0;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Insider Threat Monitor</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
            Per-user composite risk scoring across all behavioral signals.
          </p>
        </div>
        <button
          onClick={fetchAll}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Critical alert banner */}
      {criticalCount > 0 && !bannerDismissed && (
        <div className="mb-6 bg-red-600 text-white rounded-xl px-5 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 shrink-0" />
            <p className="text-sm font-medium">
              {criticalCount} user{criticalCount > 1 ? 's' : ''} at critical risk — review immediately
            </p>
          </div>
          <button
            onClick={() => setBannerDismissed(true)}
            className="text-white/80 hover:text-white shrink-0"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Summary stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm mb-1">
            <Users className="w-4 h-4" />
            Total Users Monitored
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{summary?.total_users ?? 0}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 text-red-500 text-sm mb-1">
            <AlertTriangle className="w-4 h-4" />
            Critical Risk
          </div>
          <p className="text-2xl font-bold text-red-600 dark:text-red-400">{summary?.by_risk_level.critical ?? 0}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 text-orange-500 text-sm mb-1">
            <Shield className="w-4 h-4" />
            High Risk
          </div>
          <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{summary?.by_risk_level.high ?? 0}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm mb-1">
            <Eye className="w-4 h-4" />
            Alerts This Week
            {(summary?.new_alerts_24h ?? 0) > 0 && (
              <span className="ml-1 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 font-bold leading-none">
                {summary?.new_alerts_24h}
              </span>
            )}
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{alerts.length}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700 mb-5">
        {(['scores', 'alerts'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors capitalize ${
              activeTab === tab
                ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {tab === 'scores' ? 'User Scores' : `Open Alerts${alerts.length > 0 ? ` (${alerts.length})` : ''}`}
          </button>
        ))}
      </div>

      {/* User Scores Tab */}
      {activeTab === 'scores' && (
        <div>
          {/* Filter */}
          <div className="flex gap-2 mb-4">
            {['', 'critical', 'high', 'medium', 'low'].map(level => (
              <button
                key={level}
                onClick={() => setRiskFilter(level)}
                className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors capitalize ${
                  riskFilter === level
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                {level === '' ? 'All' : level}
              </button>
            ))}
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">User</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Risk Level</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400 min-w-[160px]">Total Score</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-600 dark:text-gray-400">SaaS</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Shadow AI</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Email</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Training</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Trend</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {scores.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-10 text-center text-gray-500 dark:text-gray-400">
                        No insider threat scores computed yet.
                      </td>
                    </tr>
                  ) : (
                    scores.map(score => (
                      <tr
                        key={score.id}
                        className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900 dark:text-white">{score.user_email}</p>
                          {score.department && (
                            <p className="text-xs text-gray-400 dark:text-gray-500">{score.department}</p>
                          )}
                        </td>
                        <td className="px-4 py-3">{riskBadge(score.risk_level)}</td>
                        <td className="px-4 py-3">{scoreBar(score.total_score)}</td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-gray-700 dark:text-gray-300 font-medium">{score.saas_posture_score}</span>
                          <span className="text-gray-400 text-xs">/25</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-gray-700 dark:text-gray-300 font-medium">{score.shadow_ai_score}</span>
                          <span className="text-gray-400 text-xs">/25</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-gray-700 dark:text-gray-300 font-medium">{score.email_access_score}</span>
                          <span className="text-gray-400 text-xs">/25</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-gray-700 dark:text-gray-300 font-medium">{score.training_score}</span>
                          <span className="text-gray-400 text-xs">/25</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <TrendIcon direction={score.trend_direction} />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => setSelectedScore(score)}
                            className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Open Alerts Tab */}
      {activeTab === 'alerts' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">User</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Score at Trigger</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Top Signals</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Created</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {alerts.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-gray-500 dark:text-gray-400">
                      No open alerts.
                    </td>
                  </tr>
                ) : (
                  alerts.map(alert => (
                    <tr
                      key={alert.id}
                      className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900 dark:text-white">{alert.user_email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {riskBadge(alert.risk_level)}
                          <span className="font-bold text-gray-900 dark:text-white">{alert.score_at_trigger}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {alert.top_signals.slice(0, 3).map((sig, i) => (
                            <span
                              key={i}
                              className="inline-flex px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded"
                              title={sig.description}
                            >
                              {sig.signal_type.replace(/_/g, ' ')}
                            </span>
                          ))}
                          {alert.top_signals.length > 3 && (
                            <span className="text-xs text-gray-400 dark:text-gray-500">
                              +{alert.top_signals.length - 3} more
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">{alertStatusBadge(alert.status)}</td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                        {new Date(alert.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => setResolveAlert(alert)}
                          className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium"
                        >
                          Resolve
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail slide-out panel */}
      {selectedScore && (
        <UserDetailPanel
          score={selectedScore}
          onClose={() => setSelectedScore(null)}
          onRecompute={handleRecompute}
        />
      )}

      {/* Resolve modal */}
      {resolveAlert && (
        <ResolveAlertModal
          alert={resolveAlert}
          onClose={() => setResolveAlert(null)}
          onResolved={async () => {
            setResolveAlert(null);
            await fetchAll();
          }}
        />
      )}
    </div>
  );
}
